import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import api from '../services/api';

const ThemeContext = createContext();

export const useThemeMode = () => useContext(ThemeContext);

const isInDarkSchedule = (startHour, endHour) => {
  if (startHour === null || endHour === null) return false;
  const now = new Date();
  const hour = now.getHours();
  // Handle overnight schedules (e.g., 20:00 - 07:00)
  if (startHour > endHour) {
    return hour >= startHour || hour < endHour;
  }
  return hour >= startHour && hour < endHour;
};

export const ThemeModeProvider = ({ children }) => {
  const [preference, setPreference] = useState(() => localStorage.getItem('theme_preference') || 'light');
  const [darkScheduleStart, setDarkScheduleStart] = useState(20);
  const [darkScheduleEnd, setDarkScheduleEnd] = useState(7);
  const [scheduleLoaded, setScheduleLoaded] = useState(false);

  // Fetch global schedule from server
  useEffect(() => {
    api.get('/settings/theme-schedule')
      .then(res => {
        setDarkScheduleStart(res.data.startHour);
        setDarkScheduleEnd(res.data.endHour);
      })
      .catch(() => {
        // Use defaults if fetch fails
      })
      .finally(() => setScheduleLoaded(true));
  }, []);

  // Compute actual mode based on preference and schedule
  const computeMode = useCallback(() => {
    if (preference === 'system') {
      return isInDarkSchedule(darkScheduleStart, darkScheduleEnd) ? 'dark' : 'light';
    }
    return preference;
  }, [preference, darkScheduleStart, darkScheduleEnd]);

  const [mode, setMode] = useState(computeMode);

  // Update mode when preference or schedule changes
  useEffect(() => {
    setMode(computeMode());
  }, [computeMode]);

  // Re-check schedule every minute when in system mode
  useEffect(() => {
    if (preference !== 'system') return;
    const interval = setInterval(() => {
      setMode(computeMode());
    }, 60000);
    return () => clearInterval(interval);
  }, [preference, computeMode]);

  const setThemePreference = useCallback((pref) => {
    setPreference(pref);
    localStorage.setItem('theme_preference', pref);
  }, []);

  // Update schedule on server (admin only)
  const setSchedule = useCallback(async (start, end) => {
    try {
      await api.put('/settings/theme-schedule', { startHour: start, endHour: end });
      setDarkScheduleStart(start);
      setDarkScheduleEnd(end);
      return { success: true };
    } catch (err) {
      console.error('Failed to update theme schedule:', err);
      return { success: false, error: err.response?.data?.error || 'Failed to save' };
    }
  }, []);

  // Legacy toggle for simple light/dark switch
  const toggleMode = useCallback(() => {
    const next = preference === 'light' ? 'dark' : 'light';
    setThemePreference(next);
  }, [preference, setThemePreference]);

  return (
    <ThemeContext.Provider value={{
      mode,
      preference,
      toggleMode,
      setThemePreference,
      darkScheduleStart,
      darkScheduleEnd,
      setSchedule,
      scheduleLoaded,
    }}>
      {children}
    </ThemeContext.Provider>
  );
};
